import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from 'helpers/api'
import { isValidIBAN } from 'ibantools'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import Title from 'components/common/PageTitle/PageTitle'
import Spinner from 'components/common/Spinner/Spinner'
import { next } from 'components/Freemium/Nav/Nav'
import mainStyle from '../../FreemiumComponents.module.scss'

const IbanForm = ({ state }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [errorMessage, setErrorMessage] = useState(false)
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(false)
  const { t } = useTranslation()


  // Initialize form
  const required = t('common:form.incorrect')
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm({
    criteriaMode: 'all',
    delayError: 500,
    mode: 'onTouched',
    values: {
      owner: '',
      iban: ''
    },
  })


  // Submit form
  const onSubmit = handleSubmit(async data => {
    try {
      setIsSpinnerLoading(true)
      const { paymentPlan, contractType, calculatedPrice } = state.contract
      const {
        genderPartner,
        firstnamePartner,
        lastnamePartner,
        birthdatePartner,
        placeOfBirthPartner,
        countryOfBirthPartner
      } = state.client

      const saveID = await Promise.all(contractType.map(async (contract, contractIndex) => {
        const isMium = contractIndex === contractType.length - 1
        const endFree = new Date(state.endFree)
        const res = await api.post({
          url: '/adhesions/client',
          data: {
            ...state.listingData,
            ...state.contract,
            ...state.client,
            calculatedPrice: isMium
              ? Number(calculatedPrice.replace(',','.'))
              : 0,
            idCampaign: state.idCampaign,
            phone: state.client.phone.number,
            countryCode: state.client.phone.countryCode,
            transactionHorodate: new Date(),
            paymentType: isMium 
              ? 'direct debit by Owen'
              : 'Direct',
            paymentPlan,
            contractType: contract._id,
            contractPeriodType: contract.contractPeriodType[0]._id,
            priceType: contract.priceType[0]._id,
            rateType: contract.priceType[0].rateType[0]._id,
            iban: data.iban.replace(/\s/g,''),
            frequency: paymentPlan,
            dependent: state.client.partnerOption && {
              gender: genderPartner,
              firstname: firstnamePartner,
              lastname: lastnamePartner,
              birthdate: birthdatePartner,
              placeOfBirth: placeOfBirthPartner,
              countryOfBirth: countryOfBirthPartner
            },
            children: isMium 
              ? state.client.children
              : undefined,
            firstPaymentDate: !state.endFree && isMium
              // Free didn't start yet => mium start in the 1st in 4 months
              ? new Date(
                new Date().getFullYear(),
                new Date().getMonth() + 4,
                1
              )
              // Free already started
              : (endFree > new Date() && isMium
                // Free still active. Mium starts right after the free ends
                ? new Date(
                  endFree.getFullYear(),
                  endFree.getMonth(),
                  endFree.getDate() + 1
                )
                // Free over. Mium starts the 1st next month
                : new Date(
                  new Date().getFullYear(),
                  new Date().getMonth() + 1,
                  1
                )
              ),
            cancelationHorodate: !isMium
              // free => last day in 3 months
              ? new Date(
                new Date().getFullYear(),
                new Date().getMonth() + 4,
                0
              )
              : null
          },
          headers: { Authorization: `Bearer ${state.token}` },
        })

        return res?.response && res?.response.compliance
          ? res.response.id
          : false
      }))

      if (!saveID.includes(false)) {
        next({
          pathname, navigate, state,
          data: { saveID }
        })
      } else {
        setErrorMessage(true)
      }
    } catch (error) {
      setErrorMessage(true)
    } finally {
      setIsSpinnerLoading(false);
    }
  })


  return (
    <>
      {isSpinnerLoading && <Spinner />}
      {errorMessage && <ResponseMessage label={t('common:form.genericError')} />}

      <Title className={mainStyle['page-title']} text={<>{t('freemium:payment.ibanTitle')} 💸</>} />
      <p className={mainStyle.subtitle}>{t('freemium:payment.subtitle')}</p>

      <form className={mainStyle.form} onSubmit={onSubmit}>

        <Input
          error={errors.owner}
          register={register('owner', {
            required,
            validate: value => /[a-zA-ZÀ-ÿ-]{2,}/.test(value) || t('common:form.owner.incorrect')
          })}
          label={t('common:paymentMethod.iban.owner.label')+'*'}
          placeholder={t('common:paymentMethod.iban.owner.placeholder')}
          disallowNumbers={true}
        />
        <InputError error={errors.owner} />


        <Input
          error={errors.iban}
          register={register('iban', {
            required,
            validate: value => isValidIBAN(value.replace(/\s/g,'')) || t('common:form.iban.incorrect')
          })}
          label={'IBAN*'}
          placeholder={'IBAN'}
        />
        <InputError error={errors.iban} />


        <p className={mainStyle.required}>{t('common:form.mandatoryFields')}</p>


        <Button
          className={mainStyle.button}
          label={t('common:submit')}
          onClick={handleSubmit}
          type="primary"
          disabled={!isValid}
        />

      </form>
    </>
  )
}
export default IbanForm